import React, { forwardRef, useCallback, useMemo, useState } from 'react'
import BaseModal from "components/BaseModal";
import { FieldTypes, generateFormFields } from "utils/formUtils";
import {Form, Alert, Tooltip} from "antd";
import Vocabulary from 'components/Vocabulary';
import ProductAPI from 'restAPI/ProductAPI';
import SubProductAPI from 'restAPI/SubProductAPI';
import SubProductEnvironmentTypeAPI from 'restAPI/SubProductEnvironmentTypeAPI';
import { MODAL_SIZE_MEDIUM } from 'constants/Layout';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import axios from 'axios';
import message from 'components/CustomMessage';
import csvtojson from 'csvtojson'
import { validateCSVFile } from './utils';
import ContentOverlay from 'components/ContentOverlay';
import _ from 'lodash'
import EnvironmentName from 'views/GlobalSettings/EnvironmentName';
import ProjectAPI from 'restAPI/ProjectAPI';
import { HelpSolidIcon } from 'components/Icons';
import { removeNonPrintableUnicodeCharacters } from 'utils/fileUtils';
import { hasPermission } from 'utils/authService';
import { PermissionSetMap } from 'constants/RBACConstants';
import useEnabledFindingStatus from 'hooks/useEnabledFindingStatus';
import { useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext';
import ACSegmented from 'ac-components/ac-segmented/ACSegmented';
import FindingAPI from 'restAPI/FindingAPI';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
/**
 * CSV_ACCEPT_MIME_TYPES : possible mime types for csv files
 * csv on windows can have a different mime type depending upon the installed/default MS-EXCEL version
 * on the machine.
 */
export const CSV_ACCEPT_MIME_TYPES = [
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

const ImportFindingCSV = forwardRef((props, ref) => {
    const [data, setData] = useState(() => props.initialValues || {});
    const  [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [success, setSuccess] = useState(false);
    const [CSVFileError, setCSVFileError] = useState()
    const [disableSubmit, setDisableSubmit] = useState(true);
    const [importMode, setImportMode] = useState('single');
    const featureFlags = useFeatureFlag();

    const enabledFindingStatus = useEnabledFindingStatus();
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();

    const validateFile = useCallback((file) => {
        if(!CSV_ACCEPT_MIME_TYPES.includes(file.type)) {
            message.error("Please upload csv file only");
            return Promise.reject();
        }
        const reader = new FileReader();
        reader.readAsText(file);
        reader.onload = async function() {
            const jsonData = await csvtojson().fromString(reader.result);
            const errorList = await validateCSVFile(jsonData, enabledFindingStatus, hasDisabledEnvironment, importMode === 'multiple');
            if(!_.isEmpty(errorList)) {
                setDisableSubmit(true)
                setCSVFileError(errorList);
                setFileList([])
                form.resetFields(["csvFile"])
            }else{
                setDisableSubmit(false)
                setFileList([file]);
            }
        };
        
        return false;
    }, [enabledFindingStatus, hasDisabledEnvironment, importMode, form]);

    const submitForm = async () => {
        await form.validateFields();
        delete data.csvFile
        data.fileName = removeNonPrintableUnicodeCharacters(fileList[0].name);
        if(hasDisabledEnvironment){
            data.environment = envValue;
        }

        let resData;
        if (importMode === 'single') {
            resData = await ToolsIntegrationsAPI.postUploadUrl(data, "CUSTOM").promise;
        } else {
            resData = await FindingAPI.uploadFindingCSV_V2({
                fileName: data.fileName,
                toolName: 'CUSTOM',
            }).promise;
        }

        await axios({ method: 'put', url: resData.signedUrl || resData.data.signedUrl, data: fileList[0] });
        message.success("File uploaded successfully.");
        setSuccess(true);
    }

    const fileChange = useCallback((file) => {
        setCSVFileError();
        if(file.file.status === 'removed') {
            setFileList([])
            form.resetFields(["csvFile"])
        }
    }, [form, setCSVFileError, setFileList]);

    const importFindingInfo = "UniqueIdFromTool, Summary, Description, Mitigation, Impact and Severity” are required fields in the CSV to ingest manual findings into the ArmorCode Platform.";

    const fields = useMemo(() => [
        ...(importMode === 'multiple' ? [] : [{
        name: "product",
        label: <Vocabulary name="product" />,
        type: FieldTypes.SELECT,
        colSpan: 12,
        required: true,
        props: {
            api: ProductAPI.getAllQuick,
            onChange: () => form.setFieldsValue({subProduct: null, environment: null})
        },
    }, {
        name: "subProduct",
        label: <Vocabulary name="subProduct" />,
        type: FieldTypes.SELECT,
        colSpan: 12,
        required: true,
        props: {
            api: getSubproductList,
            apiParams: data.product,
            onChange: () => form.setFieldsValue({environment: null})
        },
    }, ...(hasDisabledEnvironment ? [] : [{
        name: "environment",
        label: "Environment",
        type: FieldTypes.SELECT,
        colSpan: 12,
        required: true,
        props: {
            api: getEnvironmentList,
            apiParams: data.subProduct,
            parseSelectOptions: customParseSelectOptions
        },
    }]), {
        name: "armorcodeProjects",
        label: <Vocabulary name="project" />,
        type: FieldTypes.MULTI_SELECT,
        colSpan: 12,
        disabled: props?.projectsDisabled || !hasPermission(PermissionSetMap.UPDATE_FINDINGS_PROJECT),
        props: {
            api: ProjectAPI.v2.getAll,
        },
    }]),
    {
        value:<span className='pull-right'><a href={importMode === 'multiple' ? '/downloads/multi_product_subproduct.csv' : '/downloads/template.csv'} download>Download Sample CSV</a> <Tooltip title={importFindingInfo}><HelpSolidIcon/></Tooltip></span>,
        type: FieldTypes.LABEL,
        noStyle: true,
    },{
        name: "csvFile",
        label: "Attach CSV file",
        type: FieldTypes.ATTACHMENT,
        required: true,
        uploadProps: {
            beforeUpload: validateFile,
            accept: ".csv",
            onChange: fileChange,
            fileList
        },
        rules: [{
            validator: (_rule, files={}) => {
                const { file } = files
                if (file) {
                    if((file.size) /1000000 > 50){
                        return Promise.reject("All file size should be less than 50 MB");
                    }
                }
                return Promise.resolve();
            }
        }]
    }], [data.product, data.subProduct, fileChange, fileList, form, hasDisabledEnvironment, importMode, props?.projectsDisabled, validateFile]);

    return(
        <BaseModal modalProps={{ title: "Import Manual Findings via CSV File", onOk: submitForm, width: MODAL_SIZE_MEDIUM, okButtonProps: {disabled: disableSubmit} }} ref={ref} afterClose={() => props.afterClose(success)}>
            <Form layout="vertical" form={form} onValuesChange={(_, values) => setData({ ...data,  ...values })} initialValues={props.initialValues}>
                <ACSegmented
                  className='m-b-sm'
                  items={[
                    { label: <>Single <Vocabulary name='product' />/<Vocabulary name='subProduct' /></>, value: "single" },
                    { label: <>Multiple <Vocabulary name='product' isPlural />/<Vocabulary name='subProduct' isPlural /></>, value: "multiple" },
                  ]}
                  onChange={setImportMode}
                  value={importMode}
                />
                {generateFormFields(fields)}
                { !_.isEmpty(CSVFileError) &&  <Alert
                 closable
                 onClose={()=> setCSVFileError()}
                 description={
                    <ContentOverlay className='markdown-text' 
                        content={
                            <ul>
                                {CSVFileError.map(err=> <li key={err}>{err}</li>)}
                            </ul>
                            } 
                        />
                    } 
                type="error" />}
            </Form>
        </BaseModal>
    )
})

export default ImportFindingCSV

function getSubproductList(productId){
    if(!productId || productId === "All"){
        return Promise.resolve([]);
    } else {
        return SubProductAPI.getAllQuick(productId);
    }
}

function getEnvironmentList(subProductId){
    if(!subProductId || subProductId === "All"){
        return Promise.resolve([]);
    } else {
        return SubProductEnvironmentTypeAPI.getAllQuick(subProductId)
    }
}

function customParseSelectOptions(options) {
    return (options || []).map(option => ({label: <EnvironmentName name={option.name} />, value: option.name}))
}